<template>
  <div>
    <div class="page-toolbar">
      <div class="page-title">客服二维码管理</div>
      <div style="display:flex; gap:10px;">
        <el-button type="primary" icon="el-icon-plus" @click="openCreateDialog">新增二维码</el-button>
        <el-button icon="el-icon-refresh" @click="loadQrs">刷新</el-button>
      </div>
    </div>

    <el-card class="page-card">
      <el-table :data="qrs" v-loading="loading" stripe>
        <el-table-column type="index" width="56" label="#" />
        <el-table-column label="二维码" width="120">
          <template slot-scope="{ row }">
            <el-image
              v-if="row.image"
              :src="row.image"
              :preview-src-list="[row.image]"
              fit="cover"
              class="qr-image"
            />
            <span v-else>-</span>
          </template>
        </el-table-column>
        <el-table-column prop="title" label="名称" min-width="180" />
        <el-table-column prop="sortOrder" label="排序" width="90" />
        <el-table-column label="状态" width="100">
          <template slot-scope="{ row }">
            <el-tag size="mini" :type="row.enabled ? 'success' : 'info'">
              {{ row.enabled ? '启用' : '停用' }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="updatedAt" label="更新时间" width="170" />
        <el-table-column label="操作" width="220" fixed="right">
          <template slot-scope="{ row }">
            <el-button size="mini" @click="openEditDialog(row)">编辑</el-button>
            <el-button size="mini" :type="row.enabled ? 'warning' : 'success'" @click="toggleEnabled(row)">
              {{ row.enabled ? '停用' : '启用' }}
            </el-button>
            <el-button size="mini" type="danger" @click="removeQr(row)">删除</el-button>
          </template>
        </el-table-column>
      </el-table>
    </el-card>

    <el-dialog :title="isEditing ? '编辑客服二维码' : '新增客服二维码'" :visible.sync="dialogVisible" width="560px">
      <el-form ref="formRef" :model="form" :rules="rules" label-width="90px">
        <el-form-item label="名称" prop="title">
          <el-input v-model.trim="form.title" placeholder="选填，便于后台区分多个二维码" maxlength="40" show-word-limit />
        </el-form-item>
        <el-form-item label="二维码图片" prop="image">
          <div class="upload-row">
            <el-input v-model.trim="form.image" placeholder="请上传二维码图片或填写图片 URL" />
            <el-button :loading="uploading" @click="triggerUpload">上传图片</el-button>
            <input ref="fileInput" class="file-input" type="file" accept="image/*" @change="handleFileChange" />
          </div>
          <div v-if="form.image" class="preview-wrap">
            <el-image :src="form.image" :preview-src-list="[form.image]" fit="cover" class="qr-image preview-image" />
          </div>
        </el-form-item>
        <el-form-item label="排序" prop="sortOrder">
          <el-input-number v-model="form.sortOrder" :min="0" :max="9999" />
        </el-form-item>
        <el-form-item label="状态" prop="enabled">
          <el-switch v-model="form.enabled" active-text="启用" inactive-text="停用" />
        </el-form-item>
      </el-form>
      <span slot="footer">
        <el-button @click="dialogVisible = false">取消</el-button>
        <el-button type="primary" :loading="saving" @click="submitForm">保存</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import {
  createCustomerServiceQr,
  deleteCustomerServiceQr,
  fetchCustomerServiceQrs,
  updateCustomerServiceQr,
  uploadAdminImage
} from '../../api/admin'
import { isAuthRedirectError } from '../../utils/auth'

const EMPTY_FORM = {
  id: '',
  title: '',
  image: '',
  sortOrder: 0,
  enabled: true
}

export default {
  name: 'CustomerServiceQrListView',
  data() {
    return {
      loading: false,
      saving: false,
      uploading: false,
      qrs: [],
      dialogVisible: false,
      isEditing: false,
      form: { ...EMPTY_FORM },
      rules: {
        image: [
          { required: true, message: '请上传二维码图片', trigger: 'blur' }
        ]
      }
    }
  },
  created() {
    this.loadQrs()
  },
  methods: {
    async loadQrs() {
      this.loading = true
      try {
        const list = await fetchCustomerServiceQrs()
        this.qrs = Array.isArray(list) ? list : []
      } catch (error) {
        if (isAuthRedirectError(error)) return
        this.$message.error(error.message || '客服二维码列表获取失败')
      } finally {
        this.loading = false
      }
    },
    openCreateDialog() {
      this.isEditing = false
      this.form = { ...EMPTY_FORM, sortOrder: this.qrs.length * 10 + 10, enabled: true }
      this.dialogVisible = true
      this.$nextTick(() => {
        if (this.$refs.formRef) this.$refs.formRef.clearValidate()
      })
    },
    openEditDialog(row) {
      this.isEditing = true
      this.form = {
        id: row.id || '',
        title: row.title || '',
        image: row.image || '',
        sortOrder: Number.isFinite(Number(row.sortOrder)) ? Number(row.sortOrder) : 0,
        enabled: !!row.enabled
      }
      this.dialogVisible = true
      this.$nextTick(() => {
        if (this.$refs.formRef) this.$refs.formRef.clearValidate()
      })
    },
    triggerUpload() {
      if (this.uploading) return
      const input = this.$refs.fileInput
      if (input) {
        input.value = ''
        input.click()
      }
    },
    async handleFileChange(event) {
      const target = event && event.target ? event.target : null
      const file = target && target.files && target.files[0] ? target.files[0] : null
      if (!file) return

      this.uploading = true
      try {
        const data = await uploadAdminImage(file, 'customer-service-qr')
        this.form.image = data.imageUrl || data.url || data.key || ''
        this.$message.success('图片上传成功')
        if (this.$refs.formRef) this.$refs.formRef.validateField('image')
      } catch (error) {
        if (isAuthRedirectError(error)) return
        this.$message.error(error.message || '图片上传失败')
      } finally {
        this.uploading = false
      }
    },
    async submitForm() {
      if (!this.$refs.formRef) return
      this.$refs.formRef.validate(async (valid) => {
        if (!valid) return
        this.saving = true
        try {
          const payload = {
            title: this.form.title || '',
            image: this.form.image,
            sortOrder: Number(this.form.sortOrder) || 0,
            enabled: !!this.form.enabled
          }
          if (this.isEditing) {
            await updateCustomerServiceQr(this.form.id, payload)
            this.$message.success('客服二维码已更新')
          } else {
            await createCustomerServiceQr(payload)
            this.$message.success('客服二维码已创建')
          }
          this.dialogVisible = false
          await this.loadQrs()
        } catch (error) {
          if (isAuthRedirectError(error)) return
          this.$message.error(error.message || '保存失败')
        } finally {
          this.saving = false
        }
      })
    },
    async toggleEnabled(row) {
      try {
        await updateCustomerServiceQr(row.id, {
          title: row.title || '',
          image: row.image || '',
          sortOrder: Number(row.sortOrder) || 0,
          enabled: !row.enabled
        })
        this.$message.success('状态已更新')
        await this.loadQrs()
      } catch (error) {
        if (isAuthRedirectError(error)) return
        this.$message.error(error.message || '状态更新失败')
      }
    },
    removeQr(row) {
      this.$confirm('确认删除该客服二维码吗？', '提示', { type: 'warning' })
        .then(async () => {
          await deleteCustomerServiceQr(row.id)
          this.$message.success('删除成功')
          await this.loadQrs()
        })
        .catch(() => {})
    }
  }
}
</script>

<style scoped>
.upload-row {
  display: flex;
  gap: 10px;
}

.file-input {
  display: none;
}

.qr-image {
  width: 84px;
  height: 84px;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
  background: #f8fafc;
}

.preview-wrap {
  margin-top: 12px;
}

.preview-image {
  width: 120px;
  height: 120px;
}
</style>
